/**
 * Settings API
 *
 * API methods for settings-related operations including
 * tracked products and Gmail integration.
 */

import { api } from './client';
import type {
  TrackedProduct,
  GmailConnectionStatus,
  EmailSenderPattern,
  GmailAccount,
  ShopifyProduct,
  ProductSearchResponse,
} from '../types';

// ============================================
// Tracked Products
// ============================================

/**
 * Get list of tracked products
 */
export async function getTrackedProducts(): Promise<TrackedProduct[]> {
  const response = await api.get<{ trackedProducts: TrackedProduct[]; updatedAt: unknown }>('/settings/products');
  return response.trackedProducts || [];
}

/**
 * Search Shopify products
 */
export async function searchProducts(
  query: string,
  limit?: number,
  cursor?: string,
  collectionHandle?: string
): Promise<ProductSearchResponse> {
  return api.get<ProductSearchResponse>('/settings/products/search', {
    q: query || undefined,
    limit,
    cursor,
    collection: collectionHandle,
  });
}

/**
 * Get a single Shopify product by ID
 */
export async function getShopifyProduct(productId: string): Promise<ShopifyProduct> {
  return api.get<ShopifyProduct>(`/settings/products/shopify/${productId}`);
}

/**
 * Add a product to tracking
 */
export async function addTrackedProduct(product: {
  shopifyProductId: string;
  shopifyProductTitle: string;
  shopifyProductHandle: string;
  productType: 'embroidery' | 'patch' | 'both' | 'undetermined';
  priceRange?: { min: string; max: string; currencyCode: string };
}): Promise<TrackedProduct> {
  const response = await api.post<{ trackedProducts: TrackedProduct[]; updatedAt: unknown }>('/settings/products', product);
  // Return the last product in the array (the newly added one)
  const products = response.trackedProducts || [];
  return products[products.length - 1];
}

/**
 * Update a tracked product's type
 */
export async function updateTrackedProductType(
  productId: string,
  productType: 'embroidery' | 'patch' | 'both' | 'undetermined'
): Promise<{ success: boolean }> {
  return api.put<{ success: boolean }>(`/settings/products/${productId}`, { productType });
}

/**
 * Remove a product from tracking
 */
export async function removeTrackedProduct(productId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/settings/products/${productId}`);
}

// ============================================
// Gmail Integration
// ============================================

/**
 * Get Gmail connection status
 */
export async function getGmailStatus(): Promise<GmailConnectionStatus> {
  return api.get<GmailConnectionStatus>('/settings/gmail');
}

/**
 * Get Gmail OAuth authorization URL
 */
export async function getGmailAuthUrl(): Promise<{ authUrl: string }> {
  return api.get<{ authUrl: string }>('/settings/gmail/auth-url');
}

/**
 * Complete Gmail OAuth callback
 */
export async function completeGmailAuth(code: string): Promise<{ success: boolean; email: string }> {
  return api.post<{ success: boolean; email: string }>('/settings/gmail/callback', { code });
}

/**
 * Disconnect Gmail account
 */
export async function disconnectGmail(): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>('/settings/gmail');
}

// ============================================
// Email Sender Patterns
// ============================================

/**
 * Get list of email sender patterns
 */
export async function getEmailSenderPatterns(): Promise<EmailSenderPattern[]> {
  return api.get<EmailSenderPattern[]>('/settings/gmail/senders');
}

/**
 * Add an email sender pattern
 */
export async function addEmailSenderPattern(pattern: {
  email: string;
  name: string;
}): Promise<EmailSenderPattern> {
  return api.post<EmailSenderPattern>('/settings/gmail/senders', pattern);
}

/**
 * Remove an email sender pattern
 */
export async function removeEmailSenderPattern(patternId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/settings/gmail/senders/${patternId}`);
}

// ============================================
// Gmail Accounts (Multi-Account Communication)
// ============================================

export async function getGmailAccounts(): Promise<GmailAccount[]> {
  return api.get<GmailAccount[]>('/settings/gmail-accounts');
}

export async function getGmailAccountAuthUrl(label: string): Promise<{ authUrl: string }> {
  return api.post<{ authUrl: string }>('/settings/gmail-accounts/auth-url', { label });
}

export async function completeGmailAccountAuth(code: string, label: string): Promise<{ success: boolean; email: string }> {
  return api.post<{ success: boolean; email: string }>('/settings/gmail-accounts/callback', { code, label });
}

export async function disconnectGmailAccount(accountId: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/settings/gmail-accounts/${accountId}`);
}

export async function updateGmailAccount(accountId: string, updates: { label?: string; isActive?: boolean }): Promise<{ success: boolean }> {
  return api.put<{ success: boolean }>(`/settings/gmail-accounts/${accountId}`, updates);
}

// ============================================
// Production Floor Configuration
// ============================================

export interface ProductionFloorStatus {
  isEnabled: boolean;
  isConfigured: boolean;
  lastUpdatedBy?: string | null;
  lastUpdatedAt?: string | null;
}

/**
 * Get production floor configuration status
 */
export async function getProductionFloorStatus(): Promise<ProductionFloorStatus> {
  return api.get<ProductionFloorStatus>('/settings/production-floor');
}

/**
 * Set or update production floor PIN
 */
export async function setProductionFloorPin(pin: string): Promise<{ success: boolean; message: string }> {
  return api.post<{ success: boolean; message: string }>('/settings/production-floor/pin', { pin });
}

/**
 * Enable or disable production floor access
 */
export async function setProductionFloorEnabled(enabled: boolean): Promise<{ success: boolean; isEnabled: boolean }> {
  return api.put<{ success: boolean; isEnabled: boolean }>('/settings/production-floor/enabled', { enabled });
}

// ============================================
// Thread Colors Configuration
// ============================================

export interface ThreadColor {
  name: string;
  hex: string;
}

/**
 * Get thread colors configuration
 */
export async function getThreadColors(): Promise<ThreadColor[]> {
  const response = await api.get<{ colors: ThreadColor[] }>('/settings/thread-colors');
  return response.colors || [];
}

/**
 * Update thread colors configuration
 */
export async function updateThreadColors(colors: ThreadColor[]): Promise<{ success: boolean; colors: ThreadColor[] }> {
  return api.put<{ success: boolean; colors: ThreadColor[] }>('/settings/thread-colors', { colors });
}

// ============================================
// QA Checklist Configuration
// ============================================

export interface QAChecklistItem {
  id: string;
  label: string;
  required: boolean;
  order: number;
}

export interface QAChecklistConfig {
  checklistItems: QAChecklistItem[];
  isEnabled: boolean;
  lastUpdatedBy?: string | null;
  lastUpdatedAt?: string | null;
}

/**
 * Get QA checklist configuration
 */
export async function getQAChecklistConfig(): Promise<QAChecklistConfig> {
  return api.get<QAChecklistConfig>('/settings/qa-checklist');
}

/**
 * Update QA checklist items
 */
export async function updateQAChecklist(checklistItems: QAChecklistItem[]): Promise<{ success: boolean; checklistItems: QAChecklistItem[] }> {
  return api.put<{ success: boolean; checklistItems: QAChecklistItem[] }>('/settings/qa-checklist', { checklistItems });
}

/**
 * Enable or disable QA floor
 */
export async function setQAFloorEnabled(enabled: boolean): Promise<{ success: boolean; isEnabled: boolean }> {
  return api.put<{ success: boolean; isEnabled: boolean }>('/settings/qa-enabled', { enabled });
}

// ============================================
// Size Selection Collections Configuration
// ============================================

export interface SizeSelectionCollection {
  id: string;
  title: string;
  handle: string;
}

/**
 * Get the list of collections configured for size selection
 */
export async function getSizeSelectionCollections(): Promise<SizeSelectionCollection[]> {
  const response = await api.get<{ collections: SizeSelectionCollection[] }>('/settings/size-selection-collections');
  return response.collections || [];
}

/**
 * Update the list of collections for size selection
 */
export async function updateSizeSelectionCollections(
  collections: SizeSelectionCollection[]
): Promise<{ success: boolean; collections: SizeSelectionCollection[] }> {
  return api.put<{ success: boolean; collections: SizeSelectionCollection[] }>(
    '/settings/size-selection-collections',
    { collections }
  );
}

/**
 * Search Shopify collections
 */
export async function searchCollections(query: string): Promise<SizeSelectionCollection[]> {
  const response = await api.get<{ collections: SizeSelectionCollection[] }>(
    '/settings/collections/search',
    { q: query }
  );
  return response.collections || [];
}

// ============================================
// Patch Collection Configuration
// ============================================

export interface PatchCollectionConfig {
  id: string;
  title: string;
  handle: string;
}

/**
 * Get the configured patch product collection
 */
export async function getPatchCollection(): Promise<PatchCollectionConfig | null> {
  const response = await api.get<{ collection: PatchCollectionConfig | null }>('/settings/patch-collection');
  return response.collection || null;
}

/**
 * Set the patch product collection
 */
export async function setPatchCollection(
  collection: PatchCollectionConfig | null
): Promise<{ success: boolean; collection: PatchCollectionConfig | null }> {
  return api.put<{ success: boolean; collection: PatchCollectionConfig | null }>(
    '/settings/patch-collection',
    { collection }
  );
}

// ============================================
// Shopify Webhooks
// ============================================

export interface WebhookInfo {
  id: string;
  topic: string;
  callbackUrl: string;
}

export interface RequiredWebhookStatus {
  topic: string;
  address: string;
  isRegistered: boolean;
  webhookId: string | null;
}

export interface WebhookStatusResponse {
  webhooks: WebhookInfo[];
  requiredWebhooks: RequiredWebhookStatus[];
  allRequired: boolean;
}

export interface WebhookRegisterResult {
  topic: string;
  success: boolean;
  error?: string;
  webhookId?: string;
}

export interface WebhookRegisterResponse {
  success: boolean;
  results: WebhookRegisterResult[];
}

/**
 * Get webhook subscription status
 */
export async function getWebhookStatus(): Promise<WebhookStatusResponse> {
  return api.get<WebhookStatusResponse>('/settings/webhooks');
}

/**
 * Register all required webhooks
 */
export async function registerWebhooks(): Promise<WebhookRegisterResponse> {
  return api.post<WebhookRegisterResponse>('/settings/webhooks/register', {});
}
